// frontend/src/pages/MovieDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Plus, Star, Calendar, MessageSquare, Clock,
  MonitorPlay, Youtube, Film, Users, Flame
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import CommentThread from '@/components/CommentThread';
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import WatchStatusSelector from '@/components/WatchStatusSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ─── FANS WIDGET ──────────────────────────────────────────────────────────────
// Shows other users who have this movie in their lists / watch statuses
function MovieFans({ tmdbId, currentUser }) {
  const [fans, setFans]     = useState([]);
  const [total, setTotal]   = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(
          `${BACKEND_URL}/api/movies/${tmdbId}/fans`,
          { withCredentials: true }
        );
        setFans(res.data.fans || []);
        setTotal(res.data.total || 0);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    if (tmdbId) load();
  }, [tmdbId]);

  if (loading) return (
    <div className="flex gap-2">
      {[1, 2, 3].map(i => (
        <div key={i} className="w-9 h-9 rounded-full skeleton-shimmer bg-muted/30" />
      ))}
    </div>
  );

  if (fans.length === 0) return (
    <p className="text-sm text-gray-500 italic">Be the first to track this!</p>
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1 flex-wrap">
        {fans.map(fan => (
          <Link key={fan.user_id} to={`/profile/${fan.user_id}`} title={fan.name}>
            <img
              src={fan.picture}
              alt={fan.name}
              className="w-9 h-9 rounded-full border-2 border-background hover:border-primary/50 transition-colors -ml-1 first:ml-0"
            />
          </Link>
        ))}
        {total > fans.length && (
          <span className="text-xs text-muted-foreground ml-2 font-semibold">
            +{total - fans.length} more
          </span>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        {total} {total === 1 ? 'person has' : 'people have'} this in their collection
      </p>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// MOVIE DETAIL PAGE
// ═══════════════════════════════════════════════════════════════════════════════

export default function MovieDetail() {
  const { id }    = useParams();
  const location  = useLocation();
  const mediaType = location.pathname.startsWith('/tv/') ? 'tv' : 'movie';

  const [movie, setMovie]               = useState(null);
  const [loading, setLoading]           = useState(true);
  const [isAddOpen, setIsAddOpen]       = useState(false);
  const [newListName, setNewListName]   = useState('');
  const [availableLists, setAvailableLists] = useState([]);
  const [comments, setComments]         = useState([]);
  const [newComment, setNewComment]     = useState('');
  const [submitting, setSubmitting]     = useState(false);
  const [currentUser, setCurrentUser]   = useState(null);

  // Get current user (needed for fans widget + watch status)
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true })
      .then(res => setCurrentUser(res.data))
      .catch(() => setCurrentUser(null));
  }, []);

  useEffect(() => {
    fetchMovieData();
    fetchUserLists();
  }, [id]);

  const fetchMovieData = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/movies/${id}?type=${mediaType}`);
      setMovie(res.data);
      fetchComments();
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/movies/${id}/comments`,
        { withCredentials: true }
      );
      setComments(res.data || []);
    } catch { /* ignore */ }
  };

  const fetchUserLists = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/my-list`, { withCredentials: true });
      const customNames = [...new Set((res.data || []).map(item => item.list_name))];
      setAvailableLists(customNames);
    } catch { /* not logged in */ }
  };

  const addToList = async (targetListName) => {
    try {
      await axios.post(`${BACKEND_URL}/api/my-list`, {
        tmdb_id:     parseInt(id),
        title:       movie.title || movie.name,
        poster_path: movie.poster_path,
        list_name:   targetListName,
      }, { withCredentials: true });
      toast.success(`Added to ${targetListName}`);
      if (!availableLists.includes(targetListName))
        setAvailableLists(prev => [...prev, targetListName]);
      setIsAddOpen(false);
      setNewListName('');
    } catch (error) {
      if (error.response?.status === 400)      toast.error('Already in that list');
      else if (error.response?.status === 401) toast.error('Please login first');
      else                                     toast.error('Failed to add to list');
    }
  };

  const handlePostComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/movies/${id}/comments`,
        { movie_id: parseInt(id), content: newComment },
        { withCredentials: true }
      );
      setNewComment('');
      fetchComments();
      toast.success('Comment posted!');
    } catch (error) {
      if (error.response?.status === 401) toast.error('Please login to comment');
      else                                toast.error('Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading state ──────────────────────────────────────────
  if (loading) return (
    <div className="min-h-screen pt-20 px-4 max-w-7xl mx-auto space-y-8">
      <Skeleton className="h-[60vh] w-full rounded-2xl bg-muted/20" />
      <div className="space-y-4">
        <Skeleton className="h-12 w-2/3 bg-muted/20" />
        <Skeleton className="h-4 w-1/3 bg-muted/20" />
        <Skeleton className="h-4 w-1/2 bg-muted/20" />
      </div>
    </div>
  );

  if (!movie) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <Film className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
        <p className="text-xl text-muted-foreground">Movie not found</p>
      </div>
    </div>
  );

  // ── Derived values ─────────────────────────────────────────
  const title           = movie.title || movie.name;
  const dateStr         = movie.release_date || movie.first_air_date;
  const fullReleaseDate = dateStr
    ? new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : 'TBA';
  const backdropUrl = movie.backdrop_path
    ? `https://image.tmdb.org/t/p/original${movie.backdrop_path}`
    : 'https://images.pexels.com/photos/7991226/pexels-photo-7991226.jpeg';
  const trailer   = movie.videos?.results?.find(v => v.type === 'Trailer' && v.site === 'YouTube');
  const providers = movie['watch/providers']?.results?.IN?.flatrate
    || movie['watch/providers']?.results?.US?.flatrate
    || [];

  return (
    <div className="min-h-screen pt-20 pb-16">

      {/* ── HERO ─────────────────────────────────────────── */}
      {/* FIX: Removed 'overflow-hidden' from the main parent container */}
      <div className="relative h-[65vh] w-full group">
        
        {/* FIX: Background layers placed in an isolated overflow-hidden wrapper z-0 */}
        <div className="absolute inset-0 overflow-hidden z-0">
          <div
            className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
            style={{ backgroundImage: `url(${backdropUrl})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/20" />
          <div className="absolute inset-0 bg-gradient-to-r from-background/60 via-transparent to-transparent" />
        </div>

        {/* Content Container gets z-10 so the dropdowns can safely overflow the hero banner */}
        <div className="absolute bottom-0 left-0 w-full p-6 md:p-12 max-w-7xl mx-auto flex flex-col md:flex-row gap-8 items-end z-10">
          {/* Poster */}
          {movie.poster_path && (
            <img
              src={`https://image.tmdb.org/t/p/w500${movie.poster_path}`}
              alt={title}
              className="w-40 md:w-52 rounded-2xl shadow-2xl border border-white/10 hidden md:block flex-shrink-0"
            />
          )}

          <div className="flex-1 mb-2 space-y-4">
            {/* Genre pills */}
            {movie.genres && (
              <div className="flex flex-wrap gap-2">
                {movie.genres.slice(0, 3).map(g => (
                  <span key={g.id}
                    className="px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-white border border-white/20 backdrop-blur-sm">
                    {g.name}
                  </span>
                ))}
              </div>
            )}

            <h1 className="font-heading text-4xl md:text-6xl font-bold text-white leading-tight tracking-tight drop-shadow-lg">
              {title}
            </h1>

            {/* Meta row */}
            <div className="flex flex-wrap items-center gap-5 text-sm text-gray-200 font-medium">
              <span className="flex items-center gap-1.5">
                <Calendar size={16} className="text-primary" /> {fullReleaseDate}
              </span>
              {movie.runtime && (
                <span className="flex items-center gap-1.5">
                  <Clock size={16} className="text-primary" /> {movie.runtime} min
                </span>
              )}
              {movie.number_of_seasons && (
                <span className="flex items-center gap-1.5">
                  <Film size={16} className="text-primary" />
                  {movie.number_of_seasons} Season{movie.number_of_seasons > 1 ? 's' : ''}
                </span>
              )}
              <span className="flex items-center gap-1.5 text-yellow-400 font-bold">
                <Star size={16} fill="currentColor" />
                {movie.vote_average?.toFixed(1)}
                <span className="text-gray-400 font-normal text-xs">
                  ({movie.vote_count?.toLocaleString()} votes)
                </span>
              </span>
            </div>

            {/* ── ACTION ROW: Add to list + Watch Status ── */}
            <div className="flex flex-wrap items-center gap-3 pt-1">
              {/* Add to collection dialog (unchanged) */}
              <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
                <DialogTrigger asChild>
                  <button className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/30 transition-all active:scale-95 btn-glow text-sm">
                    <Plus size={18} /> Add to Collection
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-card border-white/10 text-white sm:max-w-md">
                  <DialogHeader><DialogTitle>Save to List</DialogTitle></DialogHeader>
                  <div className="space-y-5 py-4">
                    <div className="grid grid-cols-2 gap-3 max-h-60 overflow-y-auto pr-1">
                      {availableLists.map(name => (
                        <Button key={name} variant="outline" onClick={() => addToList(name)}
                          className="justify-start h-11 text-sm border-white/10 hover:bg-white/5 hover:text-primary truncate">
                          {name}
                        </Button>
                      ))}
                    </div>
                    <div className="flex gap-3 items-center pt-4 border-t border-white/10">
                      <Input
                        placeholder="Create new list..."
                        value={newListName}
                        onChange={e => setNewListName(e.target.value)}
                        className="bg-background/50 border-white/10"
                        onKeyDown={e => e.key === 'Enter' && newListName.trim() && addToList(newListName)}
                      />
                      <Button onClick={() => addToList(newListName)} disabled={!newListName.trim()}>
                        Create
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* ── NEW: Manual watch status selector ── */}
              <WatchStatusSelector
                movie={movie}
                mediaType={mediaType}
                tmdbId={parseInt(id)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── MAIN CONTENT ─────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10 space-y-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">

          {/* ── LEFT: Overview + Trailer ─────────────────── */}
          <div className="lg:col-span-2 space-y-8">
            <section className="bg-card/50 p-8 rounded-2xl border border-white/5 backdrop-blur-sm">
              <h3 className="text-xl font-heading font-bold mb-4 text-white flex items-center gap-2 border-l-4 border-primary pl-3">
                Overview
              </h3>
              <p className="text-gray-300 leading-relaxed text-[17px]">
                {movie.overview || 'No overview available.'}
              </p>
            </section>

            {trailer && (
              <section className="bg-card/50 p-8 rounded-2xl border border-white/5 backdrop-blur-sm">
                <h3 className="text-xl font-heading font-bold mb-5 text-white flex items-center gap-2">
                  <Youtube className="text-red-500" /> Official Trailer
                </h3>
                <div className="aspect-video w-full rounded-xl overflow-hidden border border-white/10 shadow-xl">
                  <iframe
                    width="100%" height="100%"
                    src={`https://www.youtube.com/embed/${trailer.key}?autoplay=0&rel=0`}
                    title="Trailer"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </section>
            )}
          </div>

          {/* ── RIGHT: Streaming + Cast + Fans ───────────── */}
          <div className="space-y-8">
            {/* Where to Watch */}
            <section className="bg-card/50 p-7 rounded-2xl border border-white/5 backdrop-blur-sm">
              <h3 className="text-lg font-heading font-bold mb-5 text-white flex items-center gap-2">
                <MonitorPlay size={20} className="text-primary" /> Where to Watch
              </h3>
              {providers.length > 0 ? (
                <div className="flex flex-wrap gap-3">
                  {providers.map(p => (
                    <div key={p.provider_id} className="group relative cursor-pointer">
                      <img
                        src={`https://image.tmdb.org/t/p/w200${p.logo_path}`}
                        alt={p.provider_name}
                        className="w-14 h-14 rounded-xl border border-white/10 shadow hover:border-primary/50 transition-all"
                      />
                      <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[10px] text-muted-foreground whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                        {p.provider_name}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">Not currently streaming on tracked services.</p>
              )}
            </section>

            {/* Top Cast */}
            {movie.credits?.cast?.length > 0 && (
              <section className="bg-card/50 p-7 rounded-2xl border border-white/5 backdrop-blur-sm">
                <h3 className="text-lg font-heading font-bold mb-5 text-white">Top Cast</h3>
                <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                  {movie.credits.cast.slice(0, 8).map(actor => (
                    <div key={actor.id} className="flex items-center gap-3 group">
                      <div className="w-11 h-11 rounded-full overflow-hidden border border-white/10 bg-gray-800 flex-shrink-0">
                        {actor.profile_path && (
                          <img
                            src={`https://image.tmdb.org/t/p/w200${actor.profile_path}`}
                            alt={actor.name}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-100 group-hover:text-primary transition-colors">
                          {actor.name}
                        </p>
                        <p className="text-xs text-gray-500">{actor.character}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* ── NEW: Also loved by ───────────────────── */}
            <section className="bg-card/50 p-7 rounded-2xl border border-white/5 backdrop-blur-sm">
              <h3 className="text-lg font-heading font-bold mb-4 text-white flex items-center gap-2">
                <Flame size={18} className="text-primary" /> Also Loved By
              </h3>
              <MovieFans tmdbId={parseInt(id)} currentUser={currentUser} />
            </section>
          </div>
        </div>

        {/* ── DISCUSSION ───────────────────────────────────── */}
        <section className="bg-card/50 p-8 rounded-2xl border border-white/5 backdrop-blur-sm">
          <h3 className="text-2xl font-heading font-bold mb-8 flex items-center gap-3 text-white">
            <MessageSquare size={26} className="text-primary" />
            Discussion
            <span className="text-muted-foreground text-lg font-normal">({comments.length})</span>
          </h3>

          <form onSubmit={handlePostComment} className="mb-10">
            <div className="relative">
              <textarea
                value={newComment}
                onChange={e => setNewComment(e.target.value)}
                placeholder="Share your thoughts about this movie..."
                className="w-full bg-background/60 border border-white/10 rounded-2xl p-5 pr-36 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary/40 min-h-[100px] resize-none transition-all"
              />
              <button
                type="submit"
                disabled={submitting || !newComment.trim()}
                className="absolute bottom-4 right-4 bg-primary text-primary-foreground px-5 py-2 rounded-xl font-bold hover:opacity-90 transition-all disabled:opacity-40 text-sm active:scale-95"
              >
                {submitting ? 'Posting…' : 'Post'}
              </button>
            </div>
          </form>

          {comments.length === 0 ? (
            <div className="text-center py-14 border border-dashed border-white/10 rounded-2xl">
              <MessageSquare className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-gray-500">No comments yet. Start the conversation!</p>
            </div>
          ) : (
            <CommentThread comments={comments} movieId={id} onCommentAdded={fetchComments} />
          )}
        </section>
      </div>
    </div>
  );
}
